<template>
  <div
    v-bind="$attrs"
    v-on="$listeners"
    :style="style.container"
  >
    <div :style="style.top">
      <HeaderLargePrimary :style="style.header">
        Create survey
      </HeaderLargePrimary>
      <StepIndicatorPrimary
        :nowrap="true"
        :stepIndex="stepIndex"
        :steps="steps"
        :style="style.stepIndicator"
      />
    </div>
    <div :style="style.center">
      <BeatLoaderPrimary
        v-if="isUploading"
      />
      <div :style="getSurveyNameStyle">
        <TextRegular>
          Enter survey name.
        </TextRegular>
        <InputPrimary
          v-model="form.data.name"
          placeholder="Enter name"
          @change-input="updateFormData"
          :style="style.input"
        />
      </div>
      <div :style="getSurveyFileUploaderStyle">
        <FilePickerAreaPrimary
          v-model="form.data.file"
          :acceptedExtensions="acceptedExtensions"
          @change-file="updateFormData"
          :style="style.filePickerArea"
        >
          Drag and drop or
          <FilePickerPrimary
            v-model="form.data.file"
            :acceptedExtensions="acceptedExtensions"
            @change-file="updateFormData"
            :style="style.filePicker"
          >
            Choose File
          </FilePickerPrimary>
          to upload your skills framework. All {{ acceptedExtensions.join(', ') }} file types are supported.
        </FilePickerAreaPrimary>
        <CardNote :style="style.cardNote">
          <GoogleMaterialIconPrimary
            color="iconInfoColor"
            content="info_outline"
            :style="style.iconInfo"
          />
          <div :style="style.textInfo">
            Is this your first survey?
            <LinkPrimary
              :href="null"
              @click.stop="$emit('download-survey-template')"
              :style="style.linkDownload"
            >
              Download the template
            </LinkPrimary>
            to get started.
          </div>
        </CardNote>
      </div>
      <div :style="getSurveyReviewStyle">
        Your survey "{{ form.data.name }}" is now ready.
      </div>
    </div>
    <div :style="style.bottom">
      <ButtonSecondary
        v-if="!isLastStep"
        @click="$emit('cancel')"
      >
        Cancel
      </ButtonSecondary>
      <div
        v-else
        name="placeholder"
      />
      <ButtonPrimary
        v-if="step === 0"
        :disabled="surveyNameIsEmpty"
        @click="$emit('step-forward')"
      >
        Next
      </ButtonPrimary>
      <ButtonPrimary
        v-else-if="step === 1"
        :disabled="fileIsInvalid"
        @click="$emit('create')"
      >
        Create
      </ButtonPrimary>
      <ButtonPrimary
        v-else-if="!isUploading && isLastStep"
        @click="$emit('done')"
      >
        Done!
      </ButtonPrimary>
    </div>
  </div>
</template>

<script>
  import BeatLoaderPrimary from '@modules/v2/loader/beat-loader-primary'
  import ButtonPrimary from '@modules/v2/button/button-primary'
  import ButtonSecondary from '@modules/v2/button/button-secondary'
  import CardNote from '@modules/v2/card/card-note'
  import FilePickerAreaPrimary
    from '@modules/v2/file-picker/file-picker-area-primary'
  import FilePickerPrimary from '@modules/v2/file-picker/file-picker-primary'
  import GoogleMaterialIconPrimary
    from '@modules/v2/icon/google-material-icon-primary'
  import HeaderLargePrimary from '@modules/v2/header/header-large-primary'
  import InputPrimary from '@modules/v2/input/input-primary'
  import LinkPrimary from '@modules/v2/link/link-primary'
  import StepIndicatorPrimary
    from '@modules/v2/step-indicator/step-indicator-primary'
  import TextRegular from '@modules/v2/text/text-regular'
  import { Layout } from '@js/styles'
  import { mapGetters } from 'vuex'

  const stepToStepIndex = {
    0: 0,
    1: 1,
    2: 2,
  };

  const steps = [
    {
      label: 'Enter name',
      nodeLabel: '1',
    },
    {
      label: 'Upload data',
      nodeLabel: '2',
    },
    {
      label: 'Done!',
      nodeLabel: '3',
    },
  ];

  export default {
    name: 'FormSurveySecondary',
    inheritAttrs: false,
    components: {
      BeatLoaderPrimary,
      ButtonPrimary,
      ButtonSecondary,
      CardNote,
      FilePickerAreaPrimary,
      FilePickerPrimary,
      GoogleMaterialIconPrimary,
      HeaderLargePrimary,
      InputPrimary,
      LinkPrimary,
      StepIndicatorPrimary,
      TextRegular,
    },
    props: {
      isUploading: {
        type: Boolean,
        required: false,
        default: false,
      },
      form: {
        type: Object,
        required: false,
        default: () => {},
      },
      step: {
        type: Number,
        required: false,
        default: 0,
      },
    },
    data: function () {
      return {
        acceptedExtensions: ['.csv', '.xlsx',],
        iconInfoColor: '',
        stepToStepIndex: stepToStepIndex,
        steps: steps,
        style: {
          bottom: {
            alignItems: 'flex-end',
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          },
          cardNote: {
            alignItems: 'flex-start',
            display: 'flex',
            margin: `${Layout.space.md} 0 0 0`,
            padding: Layout.space.md,
          },
          center: {
            alignItems: 'center',
            display: 'flex',
            flex: 1,
            justifyContent: 'center',
            width: '100%',
          },
          container: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: '100%',
            width: '100%',
          },
          filePicker: {
            display: 'inline-block',
          },
          filePickerArea: {
            height: '98px',
            width: '100%',
          },
          header: {
            margin: `0 0 ${Layout.space.lg} 0`,
          },
          iconInfo: {
            margin: `${Layout.space.xs} ${Layout.space.sm} 0 0`,
          },
          input: {
            margin: `${Layout.space.md} 0 0 0`,
            width: '100%',
          },
          linkDownload: {
            display: 'inline-block',
          },
          stepIndicator: {
            alignItems: 'center',
            display: 'flex',
            padding: `0 ${Layout.space.lg}`,
          },
          textInfo: {},
          top: {},
        },
      };
    },
    created: function () {
      this.iconInfoColor = this.color.dark3();
    },
    computed: {
      ...mapGetters('colorTheme', ['color']),
      fileIsInvalid: function () {
        return this.form.data.file === null;
      },
      getSurveyFileUploaderStyle: function () {
        return {
          display: (!this.isUploading && this.step === 1) ? 'block' : 'none',
          width: '100%',
        };
      },
      getSurveyNameStyle: function () {
        return {
          display: (!this.isUploading && this.step === 0) ? 'block' : 'none',
          width: '100%',
        };
      },
      getSurveyReviewStyle: function () {
        return {
          display: (!this.isUploading && this.step === 2) ? 'block' : 'none',
          width: '100%',
        };
      },
      isLastStep: function () {
        return this.step === Object.keys(this.stepToStepIndex).length - 1;
      },
      stepIndex: function () {
        return this.stepToStepIndex[this.step];
      },
      surveyNameIsEmpty: function () {
        return this.form.data.name.trim().length === 0;
      },
    },
    methods: {
      updateFormData: function () {
        this.$emit('update:form', this.form);
      },
    },
  };
</script>
